/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { firstValueFrom, forkJoin, iif, map, of, tap, catchError } from 'rxjs';
import { GlobalStore } from '@/store/app.store';
import { CartStore } from '../cart.store';
import { CartService } from '../service/cart.service';
import { PaymentOption } from '@edt/models/checkout';
import { StorePaymentProvider } from '@medusajs/types';
import { getProviderExtraData } from '../utils/utils';

export const checkoutResolver: ResolveFn<
  { providers: PaymentOption[] } | null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
> = async (route: ActivatedRouteSnapshot) => {
  const globalStore = inject(GlobalStore);
  const cartStore = inject(CartStore);
  const cartServ = inject(CartService);

  await globalStore.loadCheckoutConfig();
  globalStore.loadOrderConfirmationConfig();

  const regionId = cartStore.regionId();
  const cart = cartStore.cart();

  if (!regionId || regionId?.length === 0 || !cart) {
    return null;
  }

  if (regionId && regionId.length > 0 && cart !== null) {
    const cartId = cart.id;
    const payCollection = cart.payment_collection;
    const $paymentCollection =
      payCollection !== undefined && payCollection !== null
        ? of(payCollection)
        : cartServ.createPaymentCollection(cartId).pipe(
            catchError(() => of(undefined)),
            map(() => cartStore.retrieveCart({ cartId, regionId }))
          );
    const payment_providers = (cartStore.cart()?.region?.payment_providers ??
      []) as unknown as StorePaymentProvider[];
    const paymentOptionsData = globalStore
      .checkout()
      ?.payment_methods?.flatMap(pm => pm.payment_option)
      .filter(pm => pm !== undefined);
    const $providers = iif(
      () => payment_providers.length > 0,
      of(payment_providers),
      cartServ.getPaymentProviders(regionId).pipe(
        catchError(() => of({ payment_providers: [] })),
        map(response => response.payment_providers)
      )
    ).pipe(
      map(providers => {
        const paymentProviders = providers.map(provider => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...rest } = getProviderExtraData(
            provider.id,
            paymentOptionsData
          );
          return {
            id: provider.id,
            active: (provider as any).is_enabled ?? false,
            ...rest,
          };
        });

        return { providers: paymentProviders };
      })
    );

    const checkShippingOptions =
      !cartStore.loadingShippingOptions() &&
      cartStore.cart()?.shipping_address &&
      cartStore.shippingOptions().length === 0
        ? true
        : false;
    const $shippingOptions = iif(
      () => checkShippingOptions,
      of(true).pipe(
        tap(() => {
          cartStore.getShippingOptions(cartId);
        })
      ),
      of(false)
    );
    return firstValueFrom(
      forkJoin([$paymentCollection, $providers, $shippingOptions]).pipe(
        map(value => {
          return value[1];
        })
      )
    );
  } else {
    return null;
  }
};
